import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../services/authService';
import { colors } from '../styles/colors';
import Pagination from './Pagination';
import ConfirmModal from './ConfirmModal';
import AddOpportunityModal from './AddOpportunityModal';

interface RecruiterOpportunity {
  id: number;
  opportunity_id: number;
  opportunity_title: string;
  poderoso: boolean;
}

interface Props {
  recruiterId: number;
  recruiterName: string;
  onClose: () => void;
}

const RecruiterOpportunitiesModal: React.FC<Props> = ({ recruiterId, recruiterName, onClose }) => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState<RecruiterOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const itemsPerPage = 5;
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [opportunityToDelete, setOpportunityToDelete] = useState<RecruiterOpportunity | null>(null);

  useEffect(() => {
    fetchOpportunities();
  }, [recruiterId, currentPage]);

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/recruiter-list/${recruiterId}/opportunities?page=${currentPage}&limit=${itemsPerPage}`);
      setOpportunities(response.data.opportunities);
      setTotalPages(response.data.pagination.totalPages);
      setTotal(response.data.pagination.total);
    } catch (error) {
      toast.error('Erro ao carregar oportunidades');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleOpportunityClick = (opportunityId: number) => {
    navigate(`/opportunities?opportunityId=${opportunityId}`);
    onClose();
  };

  const handleRemove = (opportunity: RecruiterOpportunity) => {
    setOpportunityToDelete(opportunity);
    setShowConfirmModal(true);
  };

  const confirmRemove = async () => {
    if (opportunityToDelete) {
      try {
        await api.delete(`/recruiter-list/${recruiterId}/opportunities/${opportunityToDelete.id}`);
        toast.success('Oportunidade removida com sucesso!');
        fetchOpportunities();
      } catch (error) {
        toast.error('Erro ao remover oportunidade');
      }
    }
    setShowConfirmModal(false);
    setOpportunityToDelete(null);
  };

  const cancelRemove = () => {
    setShowConfirmModal(false);
    setOpportunityToDelete(null);
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Oportunidades - {recruiterName}</h2>
          <div style={styles.headerActions}>
            <button onClick={() => setShowAddModal(true)} style={styles.addBtn}>
              Adicionar Oportunidade
            </button>
            <button onClick={onClose} style={styles.closeBtn}>×</button>
          </div>
        </div>

        {loading ? (
          <div style={styles.loading}>Carregando...</div>
        ) : (
          <div style={styles.content}>
            {opportunities.length === 0 ? (
              <div style={styles.empty}>Nenhuma oportunidade encontrada</div>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeader}>
                    <th style={styles.th}>Oportunidade</th>
                    <th style={styles.thCenter}>Poderoso</th>
                    <th style={styles.thCenter}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {opportunities.map((opportunity) => (
                    <tr key={opportunity.id} style={styles.tableRow}>
                      <td style={styles.td}>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleOpportunityClick(opportunity.opportunity_id);
                          }}
                          style={styles.opportunityLink}
                        >
                          {opportunity.opportunity_title}
                        </a>
                      </td>
                      <td style={styles.tdCenter}>
                        <span style={opportunity.poderoso ? styles.yesBadge : styles.noBadge}>
                          {opportunity.poderoso ? 'Sim' : 'Não'}
                        </span>
                      </td>
                      <td style={styles.tdCenter}>
                        <button 
                          onClick={() => handleRemove(opportunity)} 
                          style={styles.removeBtn}
                        >
                          Remover
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            
            {totalPages > 1 && (
              <div style={styles.paginationContainer}>
                <div style={styles.paginationInfo}>
                  Página {currentPage} de {totalPages} ({total} total)
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        )}
      </div>
      
      <ConfirmModal
        isOpen={showConfirmModal}
        title="Confirmar Remoção"
        message={`Tem certeza que deseja remover o recrutador da oportunidade ${opportunityToDelete?.opportunity_title}?`}
        onConfirm={confirmRemove}
        onCancel={cancelRemove}
        confirmText="Remover"
        cancelText="Cancelar"
      />
      
      {showAddModal && (
        <AddOpportunityModal
          recruiterId={recruiterId}
          onClose={() => setShowAddModal(false)}
          onSave={() => {
            setShowAddModal(false);
            fetchOpportunities();
          }}
        />
      )}
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '90%',
    maxWidth: '900px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: `1px solid ${colors.border}`,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  addBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.secondary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
  },
  title: {
    margin: 0,
    color: colors.primary,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
  },
  content: {
    padding: '1.5rem',
  },
  loading: {
    padding: '2rem',
    textAlign: 'center' as const,
  },
  empty: {
    padding: '2rem',
    textAlign: 'center' as const,
    color: colors.textLight,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  tableHeader: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  th: {
    padding: '0.75rem',
    textAlign: 'left' as const,
    fontSize: '0.9rem',
  },
  thCenter: {
    padding: '0.75rem',
    textAlign: 'center' as const,
    fontSize: '0.9rem',
  },
  tableRow: {
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: '0.75rem',
    fontSize: '0.9rem',
  },
  tdCenter: {
    padding: '0.75rem',
    fontSize: '0.9rem',
    textAlign: 'center' as const,
  },
  yesBadge: {
    backgroundColor: colors.success,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  noBadge: {
    backgroundColor: colors.error,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  paginationContainer: {
    marginTop: '1rem',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '1rem',
  },
  paginationInfo: {
    textAlign: 'center' as const,
    color: colors.textLight,
  },
  removeBtn: {
    padding: '0.4rem 0.8rem',
    backgroundColor: colors.error,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  opportunityLink: {
    color: colors.primary,
    textDecoration: 'none',
    cursor: 'pointer',
  },
};

export default RecruiterOpportunitiesModal;
